import type { JSX, ReactNode } from 'react';
import clsx from 'clsx';

export type EntityTableAlignment = 'left' | 'center' | 'right';
export type EntityTableDensity = 'comfortable' | 'compact';

export interface EntityTableColumn<TItem> {
  /** Unique identifier for the column; used as React key. */
  id: string;
  /** Header node rendered within the table head. */
  header: ReactNode;
  /** Optional additional class names applied to the header cell. */
  headerClassName?: string;
  /** Optional additional class names applied to each body cell. */
  cellClassName?: string;
  /** Text alignment for both header and body cells; defaults to left. */
  align?: EntityTableAlignment;
  /** Renderer for cell contents given the current row item. */
  renderCell: (item: TItem) => ReactNode;
}

export interface EntityTableProps<TItem> {
  items: readonly TItem[];
  columns: readonly EntityTableColumn<TItem>[];
  className?: string;
  emptyState?: ReactNode;
  getRowKey?: (item: TItem, index: number) => string;
  onRowClick?: (item: TItem, index: number) => void;
  hoverHighlight?: boolean;
  rowClassName?: string | ((item: TItem, index: number) => string | undefined);
  density?: EntityTableDensity;
}

/**
 * EntityTable renders a responsive table shell with shared dashboard styling for entity listings.
 * Provide column definitions and row data to ensure consistent spacing, typography, and hover affordances.
 */
export function EntityTable<TItem>({
  items,
  columns,
  className,
  emptyState,
  getRowKey,
  onRowClick,
  hoverHighlight = true,
  rowClassName,
  density = 'comfortable',
}: EntityTableProps<TItem>): JSX.Element {
  if (items.length === 0 && emptyState !== undefined && emptyState !== null) {
    return <>{emptyState}</>;
  }

  const resolveRowClassName = (item: TItem, index: number) => {
    if (typeof rowClassName === 'function') {
      return rowClassName(item, index);
    }
    return rowClassName;
  };

  return (
    <div className={clsx('overflow-x-auto', className)}>
      <table
        className="dashboard-entity-table min-w-full divide-y divide-surface-subtle text-left text-sm"
        data-table-density={density}
      >
        <thead className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={clsx(
                  'px-4 py-3',
                  resolveAlignmentClass(column.align),
                  column.headerClassName,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-subtle">
          {items.map((item, index) => {
            const key = getRowKey ? getRowKey(item, index) : String(index);
            const clickable = typeof onRowClick === 'function';
            return (
              <tr
                key={key}
                className={clsx(
                  resolveRowClassName(item, index),
                  hoverHighlight && 'hover:bg-brand-primary/5',
                  clickable && 'cursor-pointer',
                )}
                onClick={
                  onRowClick
                    ? (event) => {
                        const target = event.target as HTMLElement;
                        if (target.closest('a,button,input,label')) {
                          return;
                        }
                        onRowClick(item, index);
                      }
                    : undefined
                }
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={clsx(
                      'px-4 py-3 align-middle',
                      resolveAlignmentClass(column.align),
                      column.cellClassName,
                    )}
                  >
                    {column.renderCell(item)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function resolveAlignmentClass(alignment: EntityTableAlignment | undefined): string | undefined {
  switch (alignment) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    default:
      return undefined;
  }
}
